<!-- eslint-disable vue/no-mutating-props -->
<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="relative z-50" @close="emit('hideModal')">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0">
        <div
          class="fixed inset-0 bg-gray-500/75 transition-opacity"
          @click="emit('hideModal')"
          @touchstart="emit('hideModal')" />
      </TransitionChild>
      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center lg:items-center lg:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 lg:translate-y-0 lg:scale-95"
            enter-to="opacity-100 translate-y-0 lg:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 lg:scale-100"
            leave-to="opacity-0 translate-y-4 lg:translate-y-0 lg:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg border border-border bg-surface px-4 pb-4 pt-5 text-left text-foreground shadow-xl transition-all lg:my-8 w-full lg:max-w-2xl lg:p-6"
              @click.stop
              @touchstart.stop>
              <div class="text-center">
                <span class="text-xl font-semibold">{{ $t("Contact support") }}</span>
                <div class="absolute right-0 top-0 pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    class="rounded-md bg-surface text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    @click="emit('hideModal')">
                    <span class="sr-only">{{ $t("Close") }}</span>
                    <XMarkIcon class="size-6" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <hr class="my-6 border-border" />
              <form class="grid @container gap-5" @submit.prevent="handleSubmit">
                <div class="flex flex-col md:flex-row md:justify-between gap-3">
                  <Input
                    id="grid-first-name"
                    class="w-full"
                    name="customerFirstName"
                    :label="$t('First name') + '*'"
                    :placeholder="$t('First name')"
                    type="text"
                    required />
                  <Input
                    class="w-full"
                    name="customerLastName"
                    :label="$t('Last name') + '*'"
                    :placeholder="$t('Last name')"
                    type="text"
                    required />
                </div>
                <Input
                  id="grid-email"
                  name="customerEmail"
                  :label="$t('Email') + '*'"
                  :placeholder="$t('Email')"
                  type="email"
                  required />
                <Input
                  id="grid-phone"
                  name="customerPhone"
                  :label="$t('Phone number')"
                  :placeholder="$t('Phone number')"
                  type="tel" />
                <Input
                  id="grid-subject"
                  name="subject"
                  :label="$t('Subject') + '*'"
                  :placeholder="$t('Subject')"
                  type="text"
                  required />
                <Textarea
                  id="grid-comment"
                  name="customerComment"
                  :label="$t('Comment') + '*'"
                  :placeholder="$t('Comment')"
                  required />
                <Button type="submit" class="w-full" :disabled="form.isSubmitting.value || !form.meta.value.valid">
                  {{ $t("Send request") }}
                </Button>
              </form>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import { onMounted, watch } from "vue";
import brandconfig from "@/brandconfig";
import { useEventStore } from "@/store/event.js";
import { useRoute } from "vue-router";
import { useUserStore } from "@/store/user.js";
import { isB2C } from "@/brandconfig";
import { Button, Input, Textarea } from "@xs2event/js-lib/components";
import { useToast } from "@xs2event/js-lib/toast";
import { useI18n } from "vue-i18n";
import { SupportApi } from "@xs2event/js-lib/api";

import { object, string, phone, nameInput, useForm } from "@xs2event/js-lib/validation";

const route = useRoute();
const eventStore = useEventStore();
const userStore = useUserStore();
const { addToast } = useToast();
const { t } = useI18n();
const supportApi = new SupportApi();

const contactSupportFormSchema = object({
  customerFirstName: nameInput().required(t("Required")),
  customerLastName: nameInput().required(t("Required")),
  customerEmail: string().required(t("Required")).email(),
  customerPhone: phone()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .notRequired(),
  subject: string().required(t("Required")),
  customerComment: string().required(t("Required")),
  recipient: string().required(t("Required")),
});

const props = defineProps({
  open: Boolean,
  initialData: {
    type: Object,
    default: null,
  },
});
const emit = defineEmits(["hideModal"]);

const getDefaultValues = () => ({
  customerFirstName: undefined,
  customerLastName: undefined,
  customerEmail: !isB2C && userStore?.username ? userStore.username : undefined,
  customerPhone: undefined,
  subject: undefined,
  customerComment: "",
  recipient: brandconfig.BRAND == "xs2event" ? `support@xs2event.com` : `support+${brandconfig.BRAND}@xs2event.com`,
});

const getFormValues = (initialData = null) => {
  const defaults = getDefaultValues();

  if (!initialData) return defaults;

  return {
    ...defaults,
    customerFirstName: initialData.customerFirstName ?? initialData.customer_first_name ?? defaults.customerFirstName,
    customerLastName: initialData.customerLastName ?? initialData.customer_last_name ?? defaults.customerLastName,
    customerEmail: initialData.customerEmail ?? initialData.customer_email ?? defaults.customerEmail,
    customerPhone: initialData.customerPhone ?? initialData.customer_phone ?? defaults.customerPhone,
    subject: initialData.subject ?? defaults.subject,
    customerComment: initialData.customerComment ?? initialData.customer_comment ?? defaults.customerComment,
  };
};

const form = useForm({
  validationSchema: contactSupportFormSchema,
  initialValues: getFormValues(props.initialData),
  validateOnMount: true,
});

watch(
  () => props.open,
  (n) => {
    if (!n) {
      emit("hideModal");
      // Reset form when closed
      form.resetForm({ values: getFormValues() });
    } else if (n) {
      form.resetForm({ values: getFormValues(props.initialData) });
    }
  }
);

onMounted(() => {
  form.resetForm({ values: getFormValues(props.initialData) });
});

const handleSubmit = form.handleSubmit(async (values) => {
  let eventName = "";
  if (route?.params?.eventId === eventStore?.event?.slug) {
    eventName = eventStore.event?.event_name;
  }

  const payload = {
    supportEmailModel: {
      templateName: "contactform_portal.html",
      contextData: {
        customer_name: `${values.customerFirstName} ${values.customerLastName}`,
        customer_email: values.customerEmail,
        customer_phone: values.customerPhone ? values.customerPhone : "",
        subject: values.subject,
        customer_comment: values.customerComment,
        recipient: values.recipient,
        event_name: eventName ? eventName : "",
        datetime: new Date().toLocaleString(),
        distributor_name: brandconfig.BRAND,
      },
    },
  };
  try {
    await supportApi.supportsendemailPost(payload);
    emit("hideModal");
    addToast({
      title: t("Thank you!"),
      content: t("We will contact you as soon as possible!"),
      status: "success",
      duration: 5000,
    });
  } catch {
    addToast({
      title: t("Something went wrong"),
      content: t("Please contact our support desk by telephone."),
      status: "error",
      duration: 5000,
    });
  }
});
</script>
